/**
 * "Handling guide" — recipes for the edge cases every integration hits.
 * One shape per recipe: what happens → what the hosted experience does →
 * what (if anything) is yours to do, per integration model. Partner-facing
 * markdown; anchors are `#description/handling-guide` + the `###` headings.
 */
export const handlingGuide: string[] = [
  '## Handling guide',
  '',
  'What happens in the situations every integration runs into, what the hosted player experience does',
  'about them, and what — if anything — is yours to do. Each recipe names the integration model it',
  'applies to.',
  '',
  '### Insufficient balance',
  '',
  '*Hosted and custom UI.*',
  '',
  '**What happens.** A purchase costs more than the player has. The hosted experience checks the balance',
  '(your `get_balance`) before charging; if a purchase still reaches `/debit` with a stale balance, your',
  'wallet answers `error_code: "INSUFFICIENT_BALANCE"` and nothing is charged.',
  '',
  '**What we do.** The player sees *Insufficient balance*. If you passed `deposit_url` at launch, the',
  'panel also offers **Add funds**, which opens your cashier in a new tab — the game and the current',
  'round stay open. When the player returns to the tab the balance is re-read and the next attempt goes',
  'through. The same button appears in the *open next pack* flow on the reveal screen. Inside an app',
  'WebView, answer `navigate_deposit` to open your own deposit screen instead — see *Player iframe',
  'events → Lobby and cashier in a mobile app*. In parallel, the',
  'page that embeds the iframe receives `purchase_failed` with `code: "INSUFFICIENT_BALANCE"` — for the',
  'cases caught before your wallet is called as well.',
  '',
  '**What you do.**',
  '',
  '- Answer `/debit` with a JSON body carrying `error_code: "INSUFFICIENT_BALANCE"` (HTTP 200 or 4xx',
  '  both work). A non-JSON body or a connection error still refuses the purchase, but the player then',
  '  sees a generic error and the code is lost.',
  '- Pass `deposit_url` in **Create session**. For the hosted model that is the whole integration.',
  '- Prefer your own cashier on top of the game? Listen for `purchase_failed` + `code`, open it, and',
  '  post `balance_changed` when the deposit lands so the balance updates without a reload. Set',
  '  **Low balance message** to `hidden` in the Design Constructor so the game does not show its own',
  '  message under yours; a delivery whose fee the balance does not cover then sends `shipping_failed`.',
  '- Custom UI: **Instant purchase** answers `422` with `error_detail.code = "INSUFFICIENT_BALANCE"`.',
  '  Send the player to your cashier and let them retry — nothing was charged and no refund follows.',
  '',
  '### Spending limit reached',
  '',
  '*Hosted and custom UI.*',
  '',
  '**What happens.** The player has hit a spending limit kept in your wallet — daily, weekly, monthly or',
  'any other. Your `/debit` refuses the purchase and nothing is charged.',
  '',
  '**What we do.** The player sees your `error_message` as-is; without one, a default such as',
  '*You’ve reached your daily spending limit.* No **Add funds** button and no retry are offered. In',
  'parallel, the page that embeds the iframe receives `purchase_failed` with your code in `code` and the',
  'same text in `message`.',
  '',
  '**What you do.**',
  '',
  '- Answer `/debit` with `error_code: "SPENDING_LIMIT_REACHED"`, or a period-specific',
  '  `SPENDING_LIMIT_REACHED_DAY`, `SPENDING_LIMIT_REACHED_WEEK`, `SPENDING_LIMIT_REACHED_MONTH` — any',
  '  `SPENDING_LIMIT_REACHED_<PERIOD>` is passed through unchanged. HTTP 200 or 4xx both work.',
  '- Add an `error_message` written for the player, e.g. *Daily spending limit reached.* It is shown as',
  '  plain text on a single line (line breaks collapse into spaces), cut at 200 characters.',
  '- Keep `get_balance` returning the actual balance. Returning only the amount left under the limit makes',
  '  the game refuse the purchase itself, as `INSUFFICIENT_BALANCE` — see the recipe above.',
  '- Custom UI: **Instant purchase** answers `422` with your code in `error_detail.code` and the',
  '  player-facing text in `error_detail.message`.',
  '',
  '### Session expired or rejected',
  '',
  '*Hosted and custom UI.*',
  '',
  '**What happens.** A `session_token` stops being valid when a newer **Create session** is made for the',
  'same player (one active session per player) or when it expires.',
  '',
  '**What we do.** Hosted: the iframe stops, shows a *Session expired* screen and posts `session_expired`.',
  'With `lobby_url` at launch, the screen’s button takes the player back to your lobby.',
  '',
  '**What you do.** On `session_expired`, create a fresh session from your backend and reload the iframe',
  'with the new token — never reuse the old one. Custom UI: any Player API call answering',
  '`401 INVALID_TOKEN` means the same thing.',
  '',
  '### Back to your lobby',
  '',
  '*Hosted.* Pass `lobby_url` at launch. The header shows **Back to lobby** (icon-only on narrow',
  'screens) and the session-expired screen uses it too; both navigate the top window to that URL,',
  'unless your page answers `navigate_lobby` and routes the player itself (app WebViews).',
  'Without it there is no in-game exit — the player leaves the way your page allows.',
  '',
  '### Balance changed outside the game',
  '',
  '*Hosted.* The player deposits, or is credited, while the game is open. The iframe re-reads the balance',
  'whenever its tab regains focus, so a cashier in another tab needs no integration at all. If the',
  'deposit happens on your page around the iframe, post `balance_changed` for an instant refresh.',
  '',
  '### Hardware back button in a mobile app',
  '',
  '*Hosted.* In an Android WebView the system back button closes the whole WebView by default — the',
  'game switches screens without browser history, so there is nothing for the WebView to go back to.',
  'Post `navigate_back` to the iframe from your back handler: it closes the open dialog or screen and',
  'answers `navigate_back_result`. Close the WebView only on `handled: false` (the player is on the main',
  'pack screen). Details and a snippet are under **Player iframe events**.',
  '',
  '### Delivery queue is full',
  '',
  '*Hosted and custom UI.*',
  '',
  '**What happens.** Each operator has a cap on how many of ONE player\'s cards may be awaiting',
  'fulfillment at the same time — 100 by default, raised or lowered per operator on request. A card',
  'counts while its delivery is `paid` or `request_shipping`, and stops counting once the delivery',
  'ships or is canceled. The cap protects fulfillment throughput: without it a single heavy player can',
  'tie up the warehouse and slow every other player\'s deliveries down.',
  '',
  '**What we do.** Hosted: once the cap is reached the delivery form is replaced by a short notice —',
  'the cards are safe, they are being prepared, and more can be requested once those ship. Below the',
  'cap the card picker is limited to the room left, so the player cannot build a basket we would',
  'refuse. Either way **Create a delivery** is refused with `422 SHIPPING_ITEM_LIMIT_REACHED`, before',
  'anything is charged.',
  '',
  '**What you do.** Hosted: nothing. Custom UI: read `remaining_items` from **List shipping options**',
  'and cap your picker with it, and still handle `SHIPPING_ITEM_LIMIT_REACHED` on create — the queue',
  'can fill up between the two calls (a second tab, another device). Show it as a temporary state, not',
  'an account restriction, and never as a payment failure: nothing was charged.',
  '',
  '### Retried or duplicate callbacks',
  '',
  '*All models.* Every wallet callback carries a `transaction_id` and may be delivered more than once.',
  'Treat a `transaction_id` you have already processed as success — answer `DUPLICATE_TRANSACTION_ID`',
  'and we read it as success. The full rules — round lifecycle, closing flags, shipping — are under',
  '**Wallet Callbacks**.',
  '',
];
